import type { MouseEvent, TouchEvent, MutableRefObject } from "react";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { loadImage } from "../../utils";
import Debug from "../../Debug";
import { getMousePosition, getTouchPosition } from "../../../shared-logic/features/filters/utils/helpers";
import type { PosType } from "../../types/CommonTypes";

// Min amount of images that should be loaded before the components is draggable.
const MIN_LOADED_FRAMES = 6;

type CacheType = { frame: number; loaded: boolean; loading: boolean };
export type GetImageSrcType = { src: string; srcSet: string; sizes: string };

const getImageCache = (defaultFrame: number, imgCount: number): CacheType[] => {
    const imageCache = [];
    for (let i = 0; i < imgCount; i += 1) {
        imageCache.push({ loaded: false, frame: i, loading: false });
    }
    imageCache[defaultFrame].loaded = true;
    return imageCache;
};

const buildLoadOrder = (startIndex: number): number[] => {
    // Initial load order distribution.
    const initLoadOrder = [0, 6, 3, 9, 2, 8, 5, 11, 1, 7, 4, 10];
    const loadOrder = [];
    let currentIndex = startIndex;

    // Build the load order so images are loaded in a 'components' way.
    for (let i = 0; i < 12; i += 1) {
        currentIndex = (startIndex + initLoadOrder[i]) % 36;
        for (let j = 0; j < 3; j += 1) {
            loadOrder.push((currentIndex + j * 12) % 36);
        }
    }

    return loadOrder;
};

// Previous image contains data that is used for transitioning from the previous car image to the next one.
type PreviousImgType = {
    src: string;
    srcSet: string;
    sizes: string;
    frame: number;
    cacheKey: string;
};

export type UseSpinnerType = {
    isLoading: boolean;
    previousImg: PreviousImgType;
    imageCache: MutableRefObject<CacheType[]>;
    isActive: boolean;
    showPrevImg: boolean;
    currentFrame: number;
    containerProps: {
        onMouseDown: (event: MouseEvent) => void;
        onMouseMove: (event: MouseEvent) => void;
        onMouseUp: (event: MouseEvent) => void;
        onTouchStart: (event: TouchEvent) => void;
        onTouchMove: (event: TouchEvent) => void;
        onTouchEnd: (event: TouchEvent) => void;
    };
    setCurrentFrame: (currentFrame: number) => void;
    preloadImages: () => void;
    cacheImageRef: MutableRefObject<HTMLImageElement | null>;
    setFrame: (newFrame: number | ((prevFrame: number) => number)) => void;
};
const useSpinner = (
    getImageSrc: (frame: number) => GetImageSrcType,
    cacheKey: string,
    defaultFrame: number,
    // ImgCount is variable so we're able to use this logic for image sliders as well.
    imgCount: number = 36,
    onSpin?: () => void,
): UseSpinnerType => {
    // Active state is used to determine if the spinner has started loading.This can also be used for a "drag to rotate" message.
    const [isActive, setActive] = useState<boolean>(false);
    // IsLoading determines if the first required images are still being loaded.
    const [isLoading, setLoading] = useState<boolean>(false);
    const cacheImageRef = useRef<HTMLImageElement | null>(null);
    // Current frame being rendered.
    const [currentFrame, setCurrentFrame] = useState<number>(defaultFrame);
    const [previousImg, setPreviousImg] = useState<PreviousImgType>({
        ...getImageSrc(currentFrame),
        cacheKey: `${cacheKey}-${currentFrame}`,
        frame: currentFrame,
    });
    const [showPrevImg, setShowPrevImg] = useState<boolean>(false);

    // Image cache is an object containing the loaded/loading state of the 36 angles. Used to determine which image can be shown.
    const imageCache = useRef<CacheType[]>(getImageCache(currentFrame, imgCount));
    // Load order determines the best way to load car spinner images so all angles are spread out and we don't just download image 0,1,2,3,...
    const loadOrder = useRef<number[]>(buildLoadOrder(currentFrame));
    // Contains the previous cacheKey to determine if we need to trigger transition logic.
    const previousCacheKey = useRef<string>(cacheKey);
    // Contains a ref to the cacheKey, used for the loadCarImage function.
    const cacheKeyRef = useRef<string>(cacheKey);

    // This function will load a car image and update the image cache state accordingly.
    // prevFrame is the frame that was active in the previousImg state when the download started.
    // loadNext determines if the function should automatically try to download a follow up image.
    const loadCarImage = useCallback(
        async (
            frame: number,
            prevFrame: number,
            downloadCacheKey: string,
            loadNext: boolean,
            fadeAnimation: boolean = true,
        ) => {
            // Start downloading the image.
            if (!isActive || fadeAnimation) setLoading(true);
            imageCache.current[frame].loading = true;
            const { src, srcSet } = getImageSrc(frame);
            try {
                await loadImage(src, srcSet);
            } catch (ex) {
                // Gracefully handle the rejected promise
                Debug.info(`Image load failed, potential reason: ${src}?moreinfo=true`);
            }
            // If at this point the cacheKey no longer matches, do not continue as this means the active car has changed.
            if (cacheKeyRef.current !== downloadCacheKey) return;

            // Image loaded, update image cache.
            imageCache.current[frame].loading = false;
            imageCache.current[frame].loaded = true;
            if (!isActive || fadeAnimation) setLoading(false);

            // If the loaded frame matches the previously active frame, hide the previously active frame which should trigger a fade.
            if (frame === prevFrame) {
                setShowPrevImg(false);
                if (cacheImageRef.current) {
                    cacheImageRef.current.style.opacity = "1";
                    cacheImageRef.current.style.transition = "none";
                    window.requestAnimationFrame(() => {
                        if (cacheImageRef.current) {
                            cacheImageRef.current.style.opacity = "0";
                            if (fadeAnimation) {
                                cacheImageRef.current.style.transition = "opacity 0.3s";
                            }
                        }
                    });
                }
            }

            // Try to find frames which haven't loaded yet.
            const unloadedImages = imageCache.current.filter((image) => !image.loaded && !image.loading);
            if (unloadedImages.length && loadNext) {
                // If we the minimum amount of loaded images, set loading to false and active to true, allow the user to drag.
                if (
                    unloadedImages.length === 0 ||
                    unloadedImages.length < imageCache.current.length - MIN_LOADED_FRAMES
                ) {
                    setLoading(false);
                    setActive(true);
                }

                const unloadedImage = unloadedImages.sort((a, b) => {
                    const loadOrderIndexA = loadOrder.current.findIndex((loadOrderFrame) => loadOrderFrame === a.frame);
                    const loadOrderIndexB = loadOrder.current.findIndex((loadOrderFrame) => loadOrderFrame === b.frame);
                    return loadOrderIndexA - loadOrderIndexB;
                })[0];
                await loadCarImage(unloadedImage.frame, prevFrame, downloadCacheKey, loadNext, fadeAnimation);
            }
        },
        [cacheKey],
    );

    // GetPrevImg will contain the previous getImg function used for transitioning between the old and new image.
    const getPrevImg = useRef<(frame: number) => GetImageSrcType>(getImageSrc);
    useEffect(() => {
        if (cacheImageRef.current) {
            cacheImageRef.current.src = getImageSrc(currentFrame).src;
            cacheImageRef.current.srcset = getImageSrc(currentFrame).srcSet ?? "";
        }
    }, []);

    // This hook will take care of updating the state when a new car is active.
    useEffect(
        () => {
            // Only run this when the cache key is different.
            // Even though the effect has cacheKey as a dependency we still need this check as we don't want this to run on initial mount, only after an actual change in the cache key.
            if (cacheKey !== previousCacheKey.current) {
                // Update cache key and set a new image cache.
                imageCache.current = getImageCache(currentFrame, imgCount);
                cacheKeyRef.current = cacheKey;
                loadOrder.current = buildLoadOrder(currentFrame);

                // Update previous image data which will be used to animate the transition to the new car image.
                if (cacheImageRef.current) {
                    cacheImageRef.current.src = getImageSrc(currentFrame).src;
                    cacheImageRef.current.srcset = getImageSrc(currentFrame).srcSet;
                    cacheImageRef.current.style.transition = "none";
                    cacheImageRef.current.style.opacity = "1";
                }
                setPreviousImg({
                    ...getPrevImg.current(currentFrame),
                    frame: currentFrame,
                    cacheKey,
                });
                setShowPrevImg(true);
                getPrevImg.current = getImageSrc;

                // Clear loading and active states.
                setLoading(false);
                setActive(false);

                // Wait for redraw before starting to load the new car image.
                // This is necessary to get the transition to the new car image to work when the image is already cached by the browser.
                window.requestAnimationFrame(() => {
                    loadCarImage(currentFrame, currentFrame, cacheKey, false);
                    previousCacheKey.current = cacheKey;
                });
            }
        },
        // Only run this on cacheKey change as that means the config changed.
        [cacheKey],
    );

    // Refs used for dragging logic.
    const initialPos = useRef<PosType>({ x: 0, y: 0 });
    const initialFrame = useRef<number>(currentFrame);
    const dragging = useRef<boolean>(false);

    // Preload the images
    const preloadImages = async (fadeAnimation: boolean = true): Promise<void> => {
        if (!isActive) {
            // If the spinner isn't active yet, start loading the min required images for the spinner to be active.
            const loadQueue = loadOrder.current
                .slice(0, MIN_LOADED_FRAMES)
                .map((frame) => loadCarImage(frame, previousImg.frame, cacheKey, true, fadeAnimation));
            setLoading(true);
            await Promise.all(loadQueue);
        }
    };

    // This function is used to get the nearest loaded frame to the target frame.
    // If the target frame is loaded, it will return the target frame.
    const findNearestLoadedFrame = (targetFrame: number) => {
        for (let offset = 0; offset < imgCount; offset++) {
            // Calculation that ensures that the offset is always positive and within the bounds of the image count.
            // Ex. targetframe is 0 and not loaded so ofset goes to 1 -> 0 - 1 + 36 = 35, 35 % 36 = 35
            const leftFrame = (targetFrame - offset + imgCount) % imgCount;
            const rightFrame = (targetFrame + offset) % imgCount;

            if (imageCache.current[leftFrame].loaded) return leftFrame;
            if (imageCache.current[rightFrame].loaded) return rightFrame;
        }
        return currentFrame; // fallback to current frame if no loaded frame found
    };

    const initializeSpinner = useCallback(async () => {
        if (!isActive) {
            setLoading(true);
            await preloadImages(false);
            setLoading(false);
            setActive(true);
            if (onSpin) onSpin();
        }
    }, [isActive, onSpin, preloadImages]);

    const setFrame = useCallback(
        async (newFrame: number | ((prevFrame: number) => number)) => {
            await initializeSpinner();

            const nextFrame = typeof newFrame === "function" ? newFrame(currentFrame) : newFrame;
            const normalizedFrame = ((nextFrame % imgCount) + imgCount) % imgCount;

            if (imageCache.current[normalizedFrame].loaded) {
                setCurrentFrame(normalizedFrame);
            } else {
                loadCarImage(normalizedFrame, currentFrame, cacheKey, true, false);
                const nearestLoadedFrame = findNearestLoadedFrame(normalizedFrame);
                setCurrentFrame(nearestLoadedFrame);
            }
        },
        [currentFrame, cacheKey, imgCount, initializeSpinner],
    );

    // These props contain drag start logic should be inserted into the wrapping spinner container.
    const containerProps = useMemo(() => {
        // Logic used for mouse and touch start handler.
        const startDrag = async (startPos: PosType): Promise<void> => {
            await initializeSpinner();

            // Save the initial frame and position for later calculations when moving.
            initialPos.current = startPos;
            initialFrame.current = currentFrame;
            dragging.current = true;
        };

        // Set a new frame based on a new position.
        const setFrameBasedOnPos = (newPos: PosType): void => {
            const frame = (3600 + initialFrame.current + Math.round((newPos.x - initialPos.current.x) / 12)) % 36;
            if (imageCache.current[frame].loaded) window.requestAnimationFrame(() => setCurrentFrame(frame));
        };

        return {
            onMouseDown: (event: MouseEvent) => {
                event.preventDefault();
                startDrag(getMousePosition(event));
                if (cacheImageRef.current) {
                    cacheImageRef.current.style.transition = "none";
                }
            },
            onMouseMove: (event: MouseEvent) => {
                event.preventDefault();
                if (dragging.current && !isLoading) setFrameBasedOnPos(getMousePosition(event));
            },
            onMouseUp: (event: MouseEvent) => {
                event.preventDefault();
                if (dragging.current) dragging.current = false;

                if (cacheImageRef.current) {
                    cacheImageRef.current.style.transition = "opacity 0.3s";
                }
            },
            onTouchStart: (event: TouchEvent) => {
                startDrag(getTouchPosition(event));
                if (cacheImageRef.current) {
                    cacheImageRef.current.style.transition = "none";
                }
            },
            onTouchMove: (event: TouchEvent) => {
                if (!isLoading) {
                    const pos = getTouchPosition(event);
                    if (dragging.current && Math.abs(initialPos.current.y - pos.y) < 40) setFrameBasedOnPos(pos);
                    else dragging.current = false;
                }
            },
            onTouchEnd: () => {
                if (dragging.current) dragging.current = false;

                if (cacheImageRef.current) {
                    cacheImageRef.current.style.transition = "opacity 0.3s";
                }
            },
        };
    }, [cacheKey, isLoading, isActive, currentFrame]);

    return {
        isLoading,
        previousImg,
        imageCache,
        isActive,
        showPrevImg,
        currentFrame,
        containerProps,
        setCurrentFrame,
        preloadImages,
        cacheImageRef,
        setFrame,
    };
};

export default useSpinner;
